/**
 * @module ExtendCharBasedTruncationFactory
 * @date 03/08/2017
 * @copyright 2017 Empower Retirement
 * @description
 *
 *      Truncates text based on the number of characters and wraps it with more/less links to see more/less.
 *      When showing the truncated view we use ellipses.
 *
 */

import _ from "lodash";
import truncate from "truncate-html";

import StringUtil from "../../util/StringUtil";

/**
 * The constructor's parameters are "injected" via AJS's IoC.
 *
 * @constructor
 */
const ExtendCharBasedTruncationFactory = function () {
    /**
     * The HTML show more/less wrapper for the component.
     * @type {String}
     */
    const MORE_LESS_HTML =
        "" +
        "<span ng-show='!open'>{0}&nbsp;</span>" +
        "<span tabindex='0' aria-controls='planMessagesAccountOverview' role='button' ng-keydown='handleKeydownToggleShow($event)' class='btn-link ngTruncateToggleText' ng-click='toggleShow()' ng-show='!open'>{1}</span>" +
        "<span ng-show='open' id='planMessagesAccountOverview'>{2}&nbsp;<span tabindex='0' aria-controls='planMessagesAccountOverview' role='button' ng-keydown='handleKeydownToggleShow($event)' class='btn-link ngTruncateToggleText' ng-click='toggleShow()'>{3}</span></span>";

    //-----------------------------------------------------------------------
    // PROTECTED METHODS
    //-----------------------------------------------------------------------

    const removeAnchorText = function (message) {
        const anchorStartIndx = String(message).indexOf("href=");
        const anchorEndIndx = String(message).indexOf('">');
        const anchorLen = anchorEndIndx + 2 - anchorStartIndx;
        const anchorText = String(message)
            .replace(String(message).substr(anchorStartIndx, anchorLen), "")
            .replace("</a>", "");
        return { text: anchorText, textLen: anchorLen };
    };

    /**
     * Remove html tags, <br>, </br>, <b>, </b>, <p>, </p>, <div>, </div>, and line breaks
     * @param {} text
     * @returns
     */
    const removeHtmlTags = function (text) {
        let textUpd = text.replace(/<br>/g, "").replace(/<\/br>/g, "");
        textUpd = textUpd.replace(/<b>/g, "").replace(/<\/b>/g, "");
        textUpd = textUpd.replace(/<p>/g, "").replace(/<\/p>/g, "");
        textUpd = textUpd.replace(/<div>/g, "").replace(/<\/div>/g, "");
        textUpd = textUpd.replace(/(\r\n|\n|\r)/gm, "");

        return textUpd;
    };

    /** Remove a href text when calculating chars threshold
     ** Remove anchor tags to determine if the View More/View Less should be displayed **/
    const removeHrefText = function (message) {
        const hasHref = String(message).indexOf("href") > -1;
        let anchorText = "";
        if (hasHref) {
            const list = message.split("<a ");
            const count = list.length - 1;

            let text = list[0];
            for (let i = 1; i <= count; i++) {
                anchorText = removeAnchorText(list[i]);
                text += anchorText.text;
            }
            text = removeHtmlTags(text);
            return text;
        } else {
            const msgUpd = removeHtmlTags(message);
            return msgUpd;
        }
    };

    const testHrefText = function (message, threshold) {
        const hasHref = String(message).indexOf("href") > -1;
        let anchorText = { text: "", textLen: 0 };
        let anchorLen = 0;
        let updMsg = "";

        if (hasHref) {
            const list = message.split("<a ");
            const count = list.length - 1;

            let text = list[0];
            updMsg = list[0];
            for (let i = 1; i <= count; i++) {
                anchorText = removeAnchorText(list[i]);
                text += anchorText.text;
                anchorLen += anchorText.textLen;
                if (text.length > threshold) {
                    return { anchorLen: anchorLen, textLenExceedsBy: text.length - threshold };
                }
            }

            text = removeHtmlTags(text);

            if (text.length > threshold) {
                return { anchorLen: anchorLen, textLenExceedsBy: text.length - threshold };
            } else {
                return { anchorLen: 0, textLenExceedsBy: 0 };
            }
        } else {
            return { anchorLen: 0, textLenExceedsBy: 0 };
        }
    };

    /**
     * Determines if truncation should be applied based on the length of the text and
     * the threshold number.
     *
     * @param {String} text - The text being inspected for possible truncation.
     * @param {Number} threshold - The number of words to allow before truncation.
     * @returns {Boolean}
     */
    const truncationApplies = function (text, threshold) {
        text = _.trimEnd(text);
        return text.length > threshold;
    };

    const truncateHref = function (text, threshold) {
        // determine text that remains under threshold, when threshold reach, account for achor tag length
        const updatedThreshold = testHrefText(text, threshold);

        // add in length of achor tag
        const updatedText =
            updatedThreshold.textLenExceedsBy > 0
                ? text.substr(0, text.length - updatedThreshold.textLenExceedsBy) + "..."
                : text;

        const truncatedText = truncate(updatedText, threshold + updatedThreshold.anchorLen, {
            reserveLastWord: -1
        });

        return truncatedText;
    };

    /**
     * Truncates text based on the number of words and wraps it with more/less links to see more/less.
     *
     * @param {String} text - The text to apply truncation to.
     * @param {Number} threshold - The number of words to allow before truncating.
     * @param {String} customMoreLabel - The label for showing more link.
     * @param {String} customLessLabel - The label for showing less link.
     * @param {Boolean} useToggling - Indicates if the links should show.
     */
    const applyTruncation = function (
        text,
        threshold,
        customMoreLabel,
        customLessLabel,
        useToggling
    ) {
        const hasHref = String(text).indexOf("href") > -1;
        const moreContent = !hasHref
            ? truncate(text, threshold, { reserveLastWord: -1 })
            : truncateHref(text, threshold);
        const lessContent = text;
        const moreLabel = customMoreLabel ? customMoreLabel : "More";
        const lessLabel = customLessLabel ? customLessLabel : "Less";
        let result = "";

        if (useToggling) {
            const tokens = [moreContent, moreLabel, lessContent, lessLabel];
            result = StringUtil.supplant(MORE_LESS_HTML, tokens);
        } else {
            result = text.substr(0, threshold) + "...";
        }

        return result;
    };

    //-----------------------------------------------------------------------
    // EXPOSED API
    //-----------------------------------------------------------------------

    return {
        truncationApplies: truncationApplies,
        applyTruncation: applyTruncation,
        removeHrefText: removeHrefText,
        MORE_LESS_HTML: MORE_LESS_HTML
    };
};

ExtendCharBasedTruncationFactory.$inject = [];
export default ExtendCharBasedTruncationFactory;
